import { useReveal } from "@/hooks/useReveal";

const WATCH_IMG =
  "https://customer-assets.emergentagent.com/job_private-collection-12/artifacts/6yw4alm4_photo-output.jpeg";

const SPECS = [
  { label: "Centerpiece", value: "Floating Heart Diamond" },
  { label: "Movement", value: "PD Calibre 01 — Hand-wound" },
  { label: "Crystal", value: "Domed Sapphire, Anti-Reflective" },
  { label: "Case", value: "Sculpted in 18k Gold or Platinum" },
  { label: "Presentation", value: "Private Collector Vitrine" },
  { label: "Edition", value: "Strictly Limited — By Invitation" },
];

export default function FalconHeartSection({ onRequest }) {
  const eyebrow = useReveal();
  const title = useReveal();
  const lead = useReveal();
  const imgR = useReveal();
  const specR = useReveal();

  return (
    <section
      id="falcon-heart"
      data-testid="falcon-heart-section"
      className="relative bg-black py-28 md:py-40 overflow-hidden grain"
    >
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute -top-32 -right-32 w-[520px] h-[520px] rounded-full bg-[radial-gradient(circle,rgba(212,175,140,0.12)_0%,transparent_70%)]" />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
        <div className="text-center mb-16">
          <p ref={eyebrow} className="pd-reveal eyebrow" data-testid="falcon-eyebrow">
            The House Signature
          </p>
          <h2
            ref={title}
            className="pd-reveal mt-5 font-serif-display text-4xl md:text-6xl lg:text-7xl text-white tracking-[0.08em]"
            style={{ transitionDelay: "160ms" }}
            data-testid="falcon-title"
          >
            A Mechanical Heart, <br className="hidden md:block" />
            <span className="text-gold-gradient">Suspended in Time.</span>
          </h2>
          <div ref={lead} className="pd-reveal mt-8 max-w-2xl mx-auto" style={{ transitionDelay: "320ms" }}>
            <p className="font-serif-body text-lg md:text-xl text-white/75 italic leading-relaxed">
              The Falcon Heart is not measured in hours.
              It is measured in the rare hands that will ever hold one.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          <div ref={imgR} className="pd-reveal lg:col-span-7 relative">
            <div className="relative aspect-[4/5] overflow-hidden border border-[var(--pd-border)]">
              <img
                src={WATCH_IMG}
                alt="Falcon Heart timepiece"
                className="w-full h-full object-cover transition-transform duration-[2000ms] hover:scale-105"
                data-testid="falcon-watch-image"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-black/30 pointer-events-none" />
              <div className="absolute bottom-6 left-6 right-6 flex items-end justify-between">
                <div>
                  <p className="eyebrow text-white/70">Référence</p>
                  <p className="font-serif-display text-2xl text-white mt-1 tracking-[0.18em]">PD · FH-001</p>
                </div>
                <span className="text-[0.65rem] uppercase tracking-[0.4em] text-[var(--pd-gold)]">Maison Signature</span>
              </div>
            </div>
          </div>

          <div ref={specR} className="pd-reveal lg:col-span-5">
            <p className="eyebrow mb-6">The Specifications</p>
            <div className="space-y-5">
              {SPECS.map((s) => (
                <div
                  key={s.label}
                  data-testid={`spec-${s.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className="flex items-baseline justify-between gap-8 border-b border-white/10 pb-4"
                >
                  <span className="text-[0.7rem] uppercase tracking-[0.32em] text-white/55">
                    {s.label}
                  </span>
                  <span className="font-serif-body text-base md:text-lg text-white text-right">
                    {s.value}
                  </span>
                </div>
              ))}
            </div>

            <p className="mt-10 text-white/65 font-light leading-relaxed tracking-wide text-sm md:text-base">
              At the center of every Falcon Heart lies a single suspended diamond,
              held in invisible tension — a heartbeat captured beneath domed sapphire.
              Each case is hand-sculpted, each movement assembled in private,
              and each piece delivered in the silence of a collector's vitrine.
            </p>

            <button
              data-testid="falcon-request-pricing-btn"
              onClick={onRequest}
              className="btn-gold mt-10"
            >
              <span>Request Pricing</span>
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
